/**
 * Arithmétique et contrôles de saisie des trois gestes de mission.
 *
 * Sorti de l'écran pour la même raison que `CommandeEstimation.ts` : ce sont
 * des règles, elles se lisent et se vérifient sans React autour. Et surtout,
 * **aucun montant n'y devient un `number`** — tout passe par les utilitaires
 * de `format/`, qui calculent en `BigInt`. Convertir un montant en `number`
 * perdrait un token en silence au-delà de 2^53, ce que tout le back s'applique
 * à éviter.
 */
import type { Int64 } from "../api";
import {
  compareAmounts,
  isPositiveAmount,
  isZeroAmount,
  parseTokenInput,
  subAmounts,
} from "../format";

/**
 * Lit une saisie de tokens (`3`, `3,5`, `3.05`) et rend les centièmes en
 * chaîne décimale attendue par l'API.
 *
 * `parseTokenInput` tolère les espaces de groupement que l'utilisateur recopie
 * depuis l'écran et les deux séparateurs décimaux, et refuse tout le reste —
 * une troisième décimale comprise, le centième est l'unité.
 */
export function lireTokens(saisie: string): Int64 | null {
  return parseTokenInput(saisie.trim());
}

/**
 * Refus local d'une consommation, ou `null` si la saisie est recevable.
 *
 * Le contrat exige un montant **strictement positif** : une consommation de
 * zéro n'est pas une non-opération, c'est une écriture que le grand livre
 * refuserait (`entry_amount_positive`).
 *
 * Le dépassement d'enveloppe, lui, n'est **pas** contrôlé ici : le reliquat
 * affiché peut dater de quelques secondes, et c'est le service qui tranche. On
 * avertit avant l'envoi (`depassement`), on n'interdit pas d'essayer.
 */
export function erreurConsommation(saisie: string): string | null {
  if (saisie.trim() === "") {
    return "Indiquez le nombre de tokens consommés.";
  }
  const montant = lireTokens(saisie);
  if (montant === null) {
    return "Un nombre de tokens, avec au plus deux décimales : 3,5 pour trois tokens et demi.";
  }
  if (!isPositiveAmount(montant)) {
    return "Une consommation porte au moins un centième de token.";
  }
  return null;
}

/**
 * Refus local d'une imputation finale, ou `null`.
 *
 * Ici zéro est légitime, et c'est même le cas courant : « rien à imputer, on
 * clôture en l'état ».
 */
export function erreurImputation(saisie: string): string | null {
  if (saisie.trim() === "") {
    return "Indiquez 0 s'il n'y a rien à imputer.";
  }
  if (lireTokens(saisie) === null) {
    return "Un nombre de tokens, avec au plus deux décimales : 3,5 pour trois tokens et demi.";
  }
  return null;
}

/**
 * De combien le montant dépasse le reliquat, ou `null` s'il tient dedans.
 *
 * Sert à annoncer le refus **avant** l'envoi, avec le même chiffre que celui
 * que le service renverra dans son `409 envelope_overrun`.
 */
export function depassement(montant: Int64, reliquat: Int64): Int64 | null {
  return compareAmounts(montant, reliquat) > 0
    ? subAmounts(montant, reliquat)
    : null;
}

/**
 * Ce que la clôture relâchera vers le disponible : le reliquat moins
 * l'imputation finale.
 *
 * C'est le chiffre le plus parlant du produit — la conservation des tokens
 * rendue visible — et c'est pour ça qu'il est calculé avant l'envoi, pas
 * seulement constaté après. Un montant qui dépasserait le reliquat rend `"0"` :
 * le service refusera de toute façon, et afficher un relâchement négatif
 * n'apprendrait rien.
 */
export function relicatRelache(reliquat: Int64, imputation: Int64): Int64 {
  return compareAmounts(imputation, reliquat) >= 0
    ? "0"
    : subAmounts(reliquat, imputation);
}

/** Vrai quand l'enveloppe est entièrement imputée : plus rien à consommer. */
export function enveloppeEpuisee(reliquat: Int64): boolean {
  return isZeroAmount(reliquat);
}
